// What changed since a library row was verified.
//
// The database hands back the change trail since each row's last verification
// (ug.verification_changes): the row itself and the rows under it (an operation's minutes;
// a recipe's steps, lines and parts and its item's inputs, calculations and children; a job
// type's route). This file works out which fields moved, net: a field changed and changed
// back is no change, and the same minutes carried into a new standards version are none.
// It names nothing: the screen turns a field and an id into words.

import type { Resolution } from './types.ts';

export interface VerificationChangeRow {
  of_table: string;
  of_id: string;
  verified_at: string;
  table_name: string;
  row_id: string;
  action: 'update' | 'delete' | 'insert';
  changed_at: string;
  changed_by: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface VerificationChange {
  table: string;
  row_id: string;
  /** changed: one field of a row that was there. added / removed: a whole row under the verified one. */
  kind: 'changed' | 'added' | 'removed';
  /** The row as it is now (as it was, for a removed one), so the screen can name it. */
  row: Record<string, unknown>;
  field: string | null;
  was: unknown;
  now: unknown;
  /** When it last moved, and who moved it when the trail knows. */
  at: string;
  by: string | null;
}

export interface VerificationHistory {
  verified_at: string;
  changes: VerificationChange[];
}

/** Columns that say nothing about what a row is. */
const HOUSEKEEPING = new Set(['id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'verified', 'verified_at', 'verified_by', 'version', 'sort', 'source_row', 'checked_at', 'checked_by', 'unverified_since', 'unverified_by']);
/** A time standard is its minutes; the hours beside them are the same number. */
const ONLY: Record<string, string[]> = { time_standard: ['minutes_per_unit'] };

const same = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null || a === undefined || a === '') return b === null || b === undefined || b === '';
  // a numeric column reads 30 from one place and "30.0000" from another
  if ((typeof a === 'number' || typeof a === 'string') && (typeof b === 'number' || typeof b === 'string')) {
    const x = String(a).trim();
    const y = String(b).trim();
    if (x === y) return true;
    if (x !== '' && y !== '' && /^-?\d*\.?\d+$/.test(x) && /^-?\d*\.?\d+$/.test(y)) return trimNumber(x) === trimNumber(y);
    return false;
  }
  return JSON.stringify(a) === JSON.stringify(b);
};

/** "30.0000" and "30" are one number; compared as text so no decimal is ever rounded. */
function trimNumber(s: string): string {
  let t = s.startsWith('.') ? `0${s}` : s.replace(/^-\./, '-0.');
  if (t.includes('.')) t = t.replace(/0+$/, '').replace(/\.$/, '');
  t = t.replace(/^(-?)0+(?=\d)/, '$1');
  return t === '-0' ? '0' : t;
}

/** `${table}:${id}` → what has changed since that row was last verified. A row verified once whose trail holds nothing that moved is in the map with no changes: it says what it said then. */
export function changesSinceVerified(rows: VerificationChangeRow[]): Map<string, VerificationHistory> {
  const out = new Map<string, VerificationHistory>();
  const byOwner = new Map<string, VerificationChangeRow[]>();
  for (const r of rows) {
    const k = `${r.of_table}:${r.of_id}`;
    if (!byOwner.has(k)) byOwner.set(k, []);
    byOwner.get(k)!.push(r);
  }

  for (const [owner, list] of byOwner) {
    list.sort((a, b) => a.changed_at.localeCompare(b.changed_at));
    // one entry per field of a row, or per whole row added or removed; the first "was" and the last "now"
    const net = new Map<string, VerificationChange>();
    for (const r of list) {
      const fields = (keys: string[]) => (ONLY[r.table_name] ? keys.filter((k) => ONLY[r.table_name]!.includes(k)) : keys.filter((k) => !HOUSEKEEPING.has(k)));
      if (r.action === 'delete') {
        // every field change noted on it is beside the point once the row is gone
        for (const k of [...net.keys()]) if (k.startsWith(`${r.table_name}:${r.row_id}:`)) net.delete(k);
        net.set(`${r.table_name}:${r.row_id}:`, { table: r.table_name, row_id: r.row_id, kind: 'removed', row: r.before ?? {}, field: null, was: null, now: null, at: r.changed_at, by: r.changed_by });
        continue;
      }
      if (r.action === 'insert' && !ONLY[r.table_name]) {
        net.set(`${r.table_name}:${r.row_id}:`, { table: r.table_name, row_id: r.row_id, kind: 'added', row: r.after ?? {}, field: null, was: null, now: null, at: r.changed_at, by: r.changed_by });
        continue;
      }
      const before = r.before ?? {};
      const after = r.after ?? {};
      for (const f of fields(Object.keys(after))) {
        if (same(before[f], after[f])) continue;
        // a time standard at a new standards version is the same field of the same operation, moving on
        const key = ONLY[r.table_name] ? `${r.table_name}::${f}` : `${r.table_name}:${r.row_id}:${f}`;
        const seen = net.get(key);
        net.set(key, { table: r.table_name, row_id: r.row_id, kind: 'changed', row: after, field: f, was: seen ? seen.was : before[f] ?? null, now: after[f] ?? null, at: r.changed_at, by: r.changed_by });
      }
    }
    const changes = [...net.values()].filter((c) => c.kind !== 'changed' || !same(c.was, c.now));
    out.set(owner, { verified_at: list[0]!.verified_at, changes });
  }
  return out;
}

// ---------- the Engineering gate (Spencer, 2026-10-21: "a job cannot pass Engineering while anything on it is Unverified") ----------

/** A job row an AI employee touched and nobody has verified since, as the rows carry it. */
export interface UnverifiedRow {
  id: string;
  unverified_since?: string | null;
  unverified_by?: string | null;
}

/** One thing that stops a job at Engineering, with where its before/after view is. */
export interface EngineeringBlocker {
  /** A job item, a scope row, the job's own setup, or library content the job prices from. */
  kind: 'job_item' | 'scope_row' | 'job' | 'library';
  /** The table and row the Verify view opens on. */
  table: 'job_item' | 'scope_row' | 'job' | 'recipe' | 'operation' | 'material';
  id: string;
  label: string;
  /** Who left it unverified and since when, where the row says. */
  by: string | null;
  since: string | null;
  /** The job items it is on (a recipe, an operation or a material across the job), by number. */
  items: string[];
}

/**
 * What stops this job at Engineering: every job item, scope row or job setup an AI changed and nobody verified, and
 * every piece of library content the job prices from that is unverified (the recipe, an operation, a material), each
 * once. The resolution says the library side exactly; the rows say the rest.
 */
export function engineeringBlockers(
  res: Pick<Resolution, 'items'>,
  rows: { job: UnverifiedRow | null; items: (UnverifiedRow & { number: string; name: string })[]; scopeRows: (UnverifiedRow & { request_text: string })[] },
): EngineeringBlocker[] {
  const out: EngineeringBlocker[] = [];
  const numberOf = new Map(res.items.map((i) => [i.id, i.number]));
  for (const i of rows.items) {
    if (i.unverified_since) out.push({ kind: 'job_item', table: 'job_item', id: i.id, label: `${numberOf.get(i.id) ?? i.number} · ${i.name}`, by: i.unverified_by ?? null, since: i.unverified_since, items: [numberOf.get(i.id) ?? i.number] });
  }
  for (const s of rows.scopeRows) {
    if (s.unverified_since) out.push({ kind: 'scope_row', table: 'scope_row', id: s.id, label: s.request_text, by: s.unverified_by ?? null, since: s.unverified_since, items: [] });
  }
  if (rows.job?.unverified_since) out.push({ kind: 'job', table: 'job', id: rows.job.id, label: 'Job setup: a standard, the job route or a job-wide cost', by: rows.job.unverified_by ?? null, since: rows.job.unverified_since, items: [] });
  // library content, once each, with the items that use it
  const lib = new Map<string, EngineeringBlocker>();
  const add = (table: EngineeringBlocker['table'], id: string, label: string, item: string) => {
    const key = `${table}:${id}`;
    const got = lib.get(key) ?? { kind: 'library' as const, table, id, label, by: null, since: null, items: [] };
    if (!got.items.includes(item)) got.items.push(item);
    lib.set(key, got);
  };
  for (const i of res.items) {
    if (i.is_custom || !i.unverified.any) continue;
    if (i.unverified.recipe && i.recipe_id) add('recipe', i.recipe_id, `${i.catalog_item_name ?? i.name}${i.recipe_name ? ` · ${i.recipe_name} recipe` : ''}`, i.number);
    for (const s of i.unverified.steps) add('operation', s.operation_id, `Operation · ${s.operation_name}`, i.number);
    for (const l of i.unverified.lines) if (l.material_id) add('material', l.material_id, `Material · ${l.description}`, i.number);
  }
  for (const b of lib.values()) out.push(b);
  return out;
}
